import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useTheme } from '@/hooks/useTheme';
import { useI18n } from '@/hooks/useI18n';
import {
  Shield, Zap, Globe, Smartphone, CreditCard, PieChart,
  Check, Lock, Star, ArrowRight, ChevronRight,
  Building2, TrendingUp, Coins
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ROUTE_PATHS, PLANS, FAQ_ITEMS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.10 } },
};

function useCounter(end: number, duration = 2200) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let start: number;
    let raf: number;
    const step = (t: number) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      setCount(Math.floor((1 - Math.pow(1 - p, 4)) * end));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);
  return { count, ref };
}

function Counter({ end, suffix = '', prefix = '' }: { end: number; suffix?: string; prefix?: string }) {
  const { count, ref } = useCounter(end);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

export default function Home() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useI18n();
  const [activeService, setActiveService] = useState(0);

  const heroRef = useRef(null);
  const featRef = useRef(null);
  const svcRef = useRef(null);
  const plansRef = useRef(null);
  const statsRef = useRef(null);
  const testiRef = useRef(null);
  const secRef = useRef(null);
  const faqRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true });
  const featInView = useInView(featRef, { once: true, margin: '-80px' });
  const svcInView = useInView(svcRef, { once: true, margin: '-80px' });
  const plansInView = useInView(plansRef, { once: true, margin: '-80px' });
  const statsInView = useInView(statsRef, { once: true, margin: '-80px' });
  const testiInView = useInView(testiRef, { once: true, margin: '-80px' });
  const secInView = useInView(secRef, { once: true, margin: '-80px' });
  const faqInView = useInView(faqRef, { once: true, margin: '-80px' });

  const FEATURES_DATA = [
    { icon: Zap, title: t('feat_instant'), desc: t('feat_instant_desc') },
    { icon: Shield, title: t('feat_secure'), desc: t('feat_secure_desc') },
    { icon: Globe, title: t('feat_global'), desc: t('feat_global_desc') },
    { icon: Smartphone, title: t('feat_mobile'), desc: t('feat_mobile_desc') },
    { icon: CreditCard, title: t('feat_cards'), desc: t('feat_cards_desc') },
    { icon: PieChart, title: t('feat_analytics'), desc: t('feat_analytics_desc') },
  ];

  const SERVICES_DATA = [
    {
      id: 'personal',
      icon: Smartphone,
      label: t('srv_personal'),
      img: IMAGES.MOBILE_BANKING_14,
      desc: t('srv_personal_desc'),
    },
    {
      id: 'business',
      icon: Building2,
      label: t('srv_business'),
      img: IMAGES.SERVICE_BUSINESS_1,
      desc: t('srv_business_desc'),
    },
    {
      id: 'investments',
      icon: TrendingUp,
      label: t('srv_invest'),
      img: IMAGES.SERVICE_INVEST_1,
      desc: t('srv_invest_desc'),
    },
    {
      id: 'crypto',
      icon: Coins,
      label: t('srv_crypto'),
      img: IMAGES.SERVICE_CRYPTO_1,
      desc: t('srv_crypto_desc'),
    },
  ];

  const TESTIMONIALS_DATA = [
    { name: t('testimonial1_name'), role: t('testimonial1_role'), text: t('testimonial1_text') },
    { name: t('testimonial2_name'), role: t('testimonial2_role'), text: t('testimonial2_text') },
    { name: t('testimonial3_name'), role: t('testimonial3_role'), text: t('testimonial3_text') },
  ];

  const svc = SERVICES_DATA[activeService];
  const SvcIcon = svc.icon;

  return (
    <Layout>
      <section id="hero" ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden hero-mesh">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.LONDON_NIGHT_1}
            alt="London"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background" />
          <div className="absolute -top-40 right-0 w-[700px] h-[700px] glow-orb-gold opacity-50" />
          <div className="absolute bottom-0 -left-20 w-[500px] h-[500px] glow-orb-blue opacity-40" />
        </div>

        <div className="container mx-auto px-4 relative z-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              variants={stagger}
              initial="hidden"
              animate={heroInView ? 'visible' : 'hidden'}
            >
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/25 bg-gold/8 text-xs font-semibold text-gold mb-8 tracking-widest uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                FCA Regulated · FSCS Protected
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl xl:text-6xl font-bold leading-[1.08] mb-6">
                <span className="text-foreground">{t('hero_title')}</span>
                <br />
                <span className="text-gold-gradient">{t('hero_subtitle')}</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-base md:text-lg text-muted-foreground mb-10 max-w-lg leading-relaxed">
                {t('hero_desc')}
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-4 mb-12">
                <Button
                  size="lg"
                  className="btn-gold h-14 px-8 text-base rounded-xl font-bold tracking-wide"
                  onClick={() => navigate(ROUTE_PATHS.REGISTER)}
                >
                  {t('hero_cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-base rounded-xl border-border/60 text-muted-foreground hover:text-foreground hover:border-gold/40"
                  onClick={() => document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('hero_cta2')}
                </Button>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                {[
                  { icon: Shield, text: 'FCA Regulated' },
                  { icon: Lock, text: 'FSCS £85k' },
                  { icon: Star, text: '4.8 / 5 Trustpilot' },
                ].map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.text} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/40 border border-border/50 backdrop-blur-sm">
                      <Icon className="w-4 h-4 text-gold" />
                      <span className="text-sm font-medium text-foreground/80">{b.text}</span>
                    </div>
                  );
                })}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40, scale: 0.95 }}
              animate={heroInView ? { opacity: 1, x: 0, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block"
            >
              <div
                className="rounded-3xl p-6 space-y-5 max-w-sm mx-auto border"
                style={{
                  background: isDark
                    ? 'linear-gradient(135deg, oklch(0.16 0.035 255 / 0.85) 0%, oklch(0.12 0.028 255 / 0.65) 100%)'
                    : 'rgba(255,255,255,0.97)',
                  borderColor: isDark ? 'oklch(0.75 0.14 85 / 0.12)' : 'oklch(0.88 0.008 255)',
                  backdropFilter: isDark ? 'blur(20px)' : 'none',
                  boxShadow: isDark
                    ? '0 8px 40px oklch(0.04 0.01 255 / 0.7), inset 0 1px 0 oklch(0.75 0.14 85 / 0.08)'
                    : '0 8px 48px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t('hero_card_balance')}</p>
                    <p className="text-4xl font-bold text-foreground">£12,450<span className="text-gold text-2xl">.80</span></p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-gold/15 border border-gold/25 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-gold" />
                  </div>
                </div>

                <div
                  className="relative h-28 rounded-2xl overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #1a1f3a 0%, #0d1020 60%, #1a1228 100%)' }}
                >
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, transparent 30%, oklch(0.75 0.14 85 / 0.08) 50%, transparent 70%)' }} />
                  <div className="absolute inset-0 p-4 flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold tracking-widest text-white/80">CYF24 BANK</span>
                      <CreditCard className="w-5 h-5 text-amber-400/80" />
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-1 font-mono">•••• •••• •••• 4291</p>
                      <p className="text-xs text-white/60">{t('hero_card_holder')} · 09/28</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {[
                    { name: 'Netflix', cat: t('hero_card_subscription'), amt: '-£12.99', icon: '📺' },
                    { name: t('hero_card_salary'), cat: t('hero_card_income'), amt: '+£3,200', icon: '💼', pos: true },
                    { name: 'Tesco', cat: t('hero_card_shopping'), amt: '-£45.20', icon: '🛒' },
                  ].map((tx) => (
                    <div key={tx.name} className="flex items-center gap-3 py-1.5">
                      <div
                        className="w-8 h-8 rounded-xl flex items-center justify-center text-sm flex-shrink-0 border"
                        style={{
                          background: isDark ? 'oklch(0.18 0.030 255)' : 'oklch(0.95 0.005 255)',
                          borderColor: isDark ? 'oklch(0.24 0.030 255)' : 'oklch(0.88 0.008 255)',
                        }}
                      >{tx.icon}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground leading-tight">{tx.name}</p>
                        <p className="text-[10px] text-muted-foreground">{tx.cat}</p>
                      </div>
                      <p className={`text-xs font-bold flex-shrink-0 ${tx.pos ? 'text-green-500' : 'text-foreground/70'}`}>{tx.amt}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </section>

      <section id="features" ref={featRef} className="py-28 section-elevated">
        <div className="container mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" animate={featInView ? 'visible' : 'hidden'} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs text-gold font-bold tracking-widest uppercase mb-3">{t('features_title')}</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold mb-4">
              {t('features_subtitle')}
            </motion.h2>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" animate={featInView ? 'visible' : 'hidden'} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES_DATA.map((f) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} variants={fadeUp}>
                  <div className="feature-card rounded-2xl p-6 h-full group cursor-default">
                    <div className="w-12 h-12 rounded-2xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-5 group-hover:bg-gold/20 transition-colors">
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{f.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section id="services" ref={svcRef} className="py-28 section-dark overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" animate={svcInView ? 'visible' : 'hidden'} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs text-gold font-bold tracking-widest uppercase mb-3">{t('services_title')}</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold">
              {t('services_subtitle')}
            </motion.h2>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={svcInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="flex justify-center gap-3 flex-wrap mb-10">
              {SERVICES_DATA.map((s, i) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveService(i)}
                    className={`flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 border ${
                      activeService === i
                        ? 'bg-gold/15 border-gold/40 text-gold shadow-lg shadow-gold/10'
                        : 'bg-muted/30 border-border/50 text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {s.label}
                  </button>
                );
              })}
            </div>

            <motion.div
              key={activeService}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="max-w-5xl mx-auto"
            >
              <div className="glass-card rounded-3xl overflow-hidden grid md:grid-cols-2">
                <div className="relative h-64 md:h-auto min-h-[320px] overflow-hidden">
                  <img
                    src={svc.img}
                    alt={svc.label}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
                  <div className="absolute top-5 left-5">
                    <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full bg-background/50 backdrop-blur-sm border border-white/10 text-gold">
                      <SvcIcon className="w-3.5 h-3.5" />
                      {svc.label}
                    </div>
                  </div>
                </div>

                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">{svc.label}</h3>
                  <p className="text-muted-foreground mb-7 leading-relaxed">{svc.desc}</p>
                  <Button
                    className="btn-gold w-fit px-6 rounded-xl font-bold gap-2"
                    onClick={() => navigate(ROUTE_PATHS.REGISTER)}
                  >
                    {t('srv_learn_more')} <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="plans" ref={plansRef} className="py-28 section-dark">
        <div className="container mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" animate={plansInView ? 'visible' : 'hidden'} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs text-gold font-bold tracking-widest uppercase mb-3">{t('plans_title')}</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold mb-4">
              {t('plans_subtitle')}
            </motion.h2>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" animate={plansInView ? 'visible' : 'hidden'} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {PLANS.map((plan) => (
              <motion.div key={plan.name} variants={fadeUp}>
                <div className={`relative rounded-2xl p-6 h-full flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                  plan.highlighted
                    ? 'gold-border-strong bg-gradient-to-b from-gold/10 to-transparent shadow-xl shadow-gold/10'
                    : 'border border-border/50 bg-card/60'
                }`}>
                  {plan.highlighted && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="btn-gold text-xs font-bold px-4 py-1.5 rounded-full">{t('plan_popular')}</span>
                    </div>
                  )}
                  <div className="mb-6">
                    <p className={`text-sm font-bold uppercase tracking-widest mb-1 ${plan.highlighted ? 'text-gold' : 'text-muted-foreground'}`}>{plan.name}</p>
                    <div className="flex items-end gap-1">
                      <span className="text-4xl font-bold text-foreground">£{plan.price}</span>
                      <span className="text-sm text-muted-foreground pb-1">/{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-2.5 flex-1 mb-7">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-foreground/80">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlighted ? 'text-gold' : 'text-muted-foreground'}`} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => navigate(ROUTE_PATHS.REGISTER)}
                    className={`w-full rounded-xl font-bold ${plan.highlighted ? 'btn-gold' : 'bg-muted/60 text-foreground hover:bg-muted border border-border/50'}`}
                  >
                    {t('plan_get_started')}
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="stats" ref={statsRef} className="py-28 section-elevated">
        <div className="container mx-auto px-4">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={statsInView ? 'visible' : 'hidden'}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto"
          >
            {[
              { end: 500, suffix: 'k+', label: t('stats_clients') },
              { end: 190, suffix: '+', label: t('stats_countries') },
              { end: 99.99, suffix: '%', label: t('stats_uptime') },
              { end: 24, suffix: '/7', label: t('stats_support') },
            ].map((stat) => (
              <motion.div key={stat.label} variants={fadeUp}>
                <div className="stat-card rounded-2xl p-6 text-center">
                  <p className="text-4xl md:text-5xl font-bold text-gold mb-2">
                    <Counter end={stat.end} suffix={stat.suffix} />
                  </p>
                  <p className="text-sm text-muted-foreground font-medium">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="testimonials" ref={testiRef} className="py-28 section-dark">
        <div className="container mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" animate={testiInView ? 'visible' : 'hidden'} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs text-gold font-bold tracking-widest uppercase mb-3">{t('testimonials_title')}</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold">
              {t('testimonials_subtitle')}
            </motion.h2>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" animate={testiInView ? 'visible' : 'hidden'} className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TESTIMONIALS_DATA.map((t2) => (
              <motion.div key={t2.name} variants={fadeUp}>
                <div className="glass-card rounded-2xl p-6 h-full flex flex-col">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground/85 leading-relaxed flex-1 mb-5 italic">"{t2.text}"</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t2.name}</p>
                    <p className="text-xs text-muted-foreground">{t2.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="security" ref={secRef} className="py-28 section-elevated relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-background/95" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <p className="text-xs text-gold font-bold tracking-widest uppercase mb-3">{t('security_label')}</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                {t('security_title')}
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { icon: Shield, title: '256-bit SSL', desc: t('security_ssl_desc') },
                { icon: Lock, title: 'FCA Regulated', desc: t('security_fca_desc') },
                { icon: Star, title: 'FSCS Protected', desc: t('security_fscs_desc') },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="feature-card rounded-2xl p-6 text-center group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-gold/10 border border-gold/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/20 transition-colors">
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                    <p className="font-semibold text-foreground mb-2">{item.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="faq" ref={faqRef} className="py-28 section-dark">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div variants={stagger} initial="hidden" animate={faqInView ? 'visible' : 'hidden'} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs text-gold font-bold tracking-widest uppercase mb-3">FAQ</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold">
              {t('faq_title')}
            </motion.h2>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={faqInView ? { opacity: 1 } : {}} transition={{ duration: 0.6, delay: 0.3 }}>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="glass-card rounded-2xl px-5 border-none"
                >
                  <AccordionTrigger className="text-sm font-semibold text-foreground py-4 hover:no-underline hover:text-gold transition-colors [&[data-state=open]]:text-gold">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      <section id="cta" className="py-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gold/8 via-background to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] glow-orb-gold opacity-40" />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs text-gold font-bold tracking-widest uppercase mb-4">{t('cta_label')}</p>
            <h2 className="text-4xl md:text-6xl font-bold mb-5">
              {t('cta_title')}
            </h2>
            <p className="text-muted-foreground text-xl mb-10 max-w-lg mx-auto">
              {t('cta_desc')}
            </p>
            <Button
              size="lg"
              className="btn-gold h-16 px-12 text-lg rounded-2xl font-bold gap-3 shadow-2xl shadow-gold/20"
              onClick={() => navigate(ROUTE_PATHS.REGISTER)}
            >
              {t('cta_btn')}
              <ArrowRight className="w-6 h-6" />
            </Button>
            <p className="text-xs text-muted-foreground mt-5">
              {t('cta_footer_note')}
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}